import {
  Redirect,
  useLocalSearchParams,
  useGlobalSearchParams,
} from 'expo-router';
import * as Linking from 'expo-linking';
import { useEffect, useState } from 'react';
import { View } from 'react-native';
import { Text } from '@/components/ui/text';
import { persistentStorage } from '@/lib/storage';
import { authVar } from '@/lib/apollo/auth';
import { logger } from '@/lib/logger';
import type { E2ETier } from '@/lib/e2e/mint-e2e-token';
import { decodeAuthTokens } from '@/lib/e2e/auth-token-codec';
import type { AuthTokens } from '@/lib/types/auth';

// E2E-only route. Given a tier (`fytness://e2e/write-token?tier=free|pro|ai`) it mints a
// real Zitadel session for that tier's service account IN-APP and writes it into the same
// place the real login persists it — so Maestro flows launch authenticated on any platform.
// The tier is deep-linked (not the ~1.1k-char token) because Maestro's `openLink` drops long
// URLs on the iOS simulator (mobile-dev-inc/Maestro#2610); a few chars arrive reliably.
//
// ⚠ Auth-injection surface. INERT unless the build was compiled with EXPO_PUBLIC_E2E_ENABLED
// === 'true' — set ONLY by the `e2e` build profile, NEVER staging/production/store. The SA
// keys the minter uses are likewise present only in the `e2e` env, and this whole branch is
// dead-code-eliminated (with the minter + keys) from non-e2e bundles. (__DEV__ gating would
// not work: the release build Maestro runs against has __DEV__ === false.)
const E2E_ENABLED = process.env.EXPO_PUBLIC_E2E_ENABLED === 'true';

const VALID_TIERS: readonly E2ETier[] = ['free', 'pro', 'ai'];

export default function E2EWriteToken() {
  // A deep-link param can miss useLocalSearchParams during navigation timing; fall back to
  // global params, then the raw URL.
  const local = useLocalSearchParams<{ tier?: string; data?: string }>();
  const global = useGlobalSearchParams<{ tier?: string; data?: string }>();
  const url = Linking.useURL();
  const urlParams = url ? Linking.parse(url).queryParams : undefined;
  // iOS deep-links a short `tier` (Maestro drops the ~1.1k-char token on the sim, #2610) and
  // mints in-app; web (https, no long-URL limit, and no SA keys in the web build) passes the
  // already-minted session as `data`. Either may be present — data takes precedence.
  const tier =
    local.tier ?? global.tier ?? (urlParams?.tier as string | undefined);
  const data =
    local.data ?? global.data ?? (urlParams?.data as string | undefined);

  const [done, setDone] = useState(false);
  const [phase, setPhase] = useState('mount');
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    if (!E2E_ENABLED) {
      setPhase('e2e-disabled');
      return;
    }
    if (!data && !tier) {
      setPhase('waiting-for-param');
      return;
    }
    if (!data && !VALID_TIERS.includes(tier as E2ETier)) {
      setErr(`invalid tier "${tier}"`);
      setPhase('error');
      setDone(true);
      return;
    }
    (async () => {
      try {
        // `data` (pre-minted, web) wins over `tier` (in-app mint, iOS).
        let tokens: AuthTokens;
        if (data) {
          setPhase('decoding data');
          tokens = decodeAuthTokens(data);
        } else {
          setPhase(`minting (${tier})`);
          // Env-literal-gated dynamic import so the in-app minter (and its `node-forge`
          // dep) dead-code-eliminates out of non-e2e bundles — Metro inlines the
          // `process.env` literal, dropping this branch and its `import()`. The route
          // already redirects when the flag is off, so the else is unreachable at runtime.
          if (process.env.EXPO_PUBLIC_E2E_ENABLED === 'true') {
            const { mintE2EToken } = await import('@/lib/e2e/mint-e2e-token');
            tokens = await mintE2EToken(tier as E2ETier);
          } else {
            throw new Error('e2e minting disabled');
          }
        }
        setPhase('writing session');
        await persistentStorage.set('auth', tokens);
        authVar(tokens);
        setPhase('done; redirecting');
      } catch (error) {
        logger.error({ err: error }, '[e2e] Failed to mint/write auth token');
        setErr(
          error instanceof Error
            ? `${error.name}: ${error.message}`
            : String(error),
        );
        setPhase('error');
      } finally {
        setDone(true);
      }
    })();
  }, [tier, data]);

  // Disabled build (production) or malformed request → behave like an unknown route.
  if (!E2E_ENABLED) return <Redirect href="/" />;
  // Once seeded, let the index route forward to the (private) area. Stay on this screen on
  // error so the failure is visible.
  if (done && !err) return <Redirect href="/" />;

  return (
    <View
      testID="e2e-write-token"
      className="flex-1 items-center justify-center bg-background p-6 gap-3"
    >
      <Text className="text-text-muted">Seeding E2E session…</Text>
      <Text
        testID="e2e-write-token-phase"
        className="text-xs text-text-muted text-center"
      >
        phase: {phase} · {data ? 'data' : `tier=${tier ?? 'MISSING'}`}
      </Text>
      {err ? (
        <Text
          testID="e2e-write-token-error"
          className="text-xs text-destructive text-center"
        >
          {err}
        </Text>
      ) : null}
    </View>
  );
}
